import { MaterialCommunityIcons } from "@expo/vector-icons";
import * as Sentry from "@sentry/react-native";
import type { MergedPocDuty, PocDutyChangeReason, PocFrequency } from "@taskhealth/shared";
import { isValidPocFrequency, POC_DUTY_CHANGE_REASONS } from "@taskhealth/shared";
import { LinearGradient } from "expo-linear-gradient";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    KeyboardAvoidingView,
    LayoutAnimation,
    Platform,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    UIManager,
    View
} from "react-native";
import { Text, TextInput } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { getCategoryIcon } from "./poc-icons";
import type { AutosaveStatus } from "./PocReviewStep";

import { colors, fonts, gradients, radius, shadows } from "@/lib/theme";

// LayoutAnimation requires this experimental flag on Android. Idempotent —
// the repo also enables it in VoiceRecorder.tsx for the same reason.
if (Platform.OS === "android" && UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
}

// ---------------------------------------------------------------------------
// Frequency parsing / serialization
// ---------------------------------------------------------------------------

type FreqType = "every" | "needed" | "dpw";

function parseFrequency(freq: string): { type: FreqType; dpw: number } {
    if (freq === "Every visit") {
        return { type: "every", dpw: 1 };
    }
    if (freq === "As needed") {
        return { type: "needed", dpw: 1 };
    }
    const match = freq.match(/^(\d+) visits per week$/);
    if (match) {
        return { type: "dpw", dpw: parseInt(match[1], 10) };
    }
    // Unknown format — silent fallback to "Every visit" would round-trip the
    // raw unknown string back to the server unchanged on next save. Surface
    // via Sentry so we notice when the server adds a new frequency value
    // before the client supports it.
    Sentry.addBreadcrumb({
        category: "poc",
        level: "warning",
        message: "Unknown POC frequency format",
        data: { rawFrequency: freq }
    });
    return { type: "every", dpw: 1 };
}

function serializeFrequency(type: FreqType, dpw: number): PocFrequency {
    if (type === "every") {
        return "Every visit";
    }
    if (type === "needed") {
        return "As needed";
    }
    const freq = `${dpw} visits per week`;
    if (isValidPocFrequency(freq)) {
        return freq;
    }
    return "Every visit"; // fallback for out-of-range
}

// ---------------------------------------------------------------------------
// Category order — must match the 6 master categories in poc_master_duties (DB)
// ---------------------------------------------------------------------------

const CATEGORY_ORDER = [
    "Personal Care",
    "Toileting",
    "Nutrition",
    "Activities",
    "Treatment",
    "Household"
];

function dutyKey(duty: MergedPocDuty): string {
    return duty.id != null ? `id:${duty.id}` : `name:${duty.name}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PocDutyListProps {
    duties: MergedPocDuty[];
    /**
     * True on a GENERAL special visit, where this plan was carried forward from
     * the one already on file rather than written by a model. The badges must
     * say so: labelling a carried-forward duty "AI" tells the nurse a model
     * chose it, and she would weigh it differently — it is the previous
     * nurse's clinical decision, not a suggestion.
     */
    carryForward?: boolean;
    specialInstructions: string;
    /** Generated baseline keyed by stable duty identity. LLM selects duties; agency settings set frequency. */
    aiBaseline: Map<string, { selected: boolean; frequency: string }> | null;
    /** Bottom-bar autosave indicator status. */
    autosaveStatus: AutosaveStatus;
    onToggleDuty: (index: number) => void;
    onChangeFrequency: (index: number, frequency: PocFrequency) => void;
    onChangeDutyExplanation: (
        index: number,
        explanation: {
            rnChangeReasonCodes: PocDutyChangeReason[];
            rnChangeReasonNote: string | null;
        }
    ) => void;
    onChangeInstructions: (text: string) => void;
    /** Batched mutation for Select All / Deselect All. One setDuties call,
     *  one debounced autosave. */
    onSetCategorySelection: (keys: Set<string>, selected: boolean) => void;
    onConfirm: () => void;
    saving: boolean;
    readOnly: boolean;
    readOnlyReason?: string;
}

// ---------------------------------------------------------------------------
// Duty Card
// ---------------------------------------------------------------------------

interface DutyCardProps {
    duty: MergedPocDuty;
    /** See PocDutyListProps.carryForward — changes the per-duty badge wording. */
    carryForward?: boolean;
    index: number;
    originalState: { selected: boolean; frequency: string } | undefined;
    onToggle: (index: number) => void;
    onChangeFrequency: (index: number, frequency: PocFrequency) => void;
    onChangeExplanation: (
        index: number,
        explanation: {
            rnChangeReasonCodes: PocDutyChangeReason[];
            rnChangeReasonNote: string | null;
        }
    ) => void;
    readOnly: boolean;
}

const DutyCard = React.memo(
    function DutyCard({
        duty,
        carryForward = false,
        index,
        originalState,
        onToggle,
        onChangeFrequency,
        onChangeExplanation,
        readOnly
    }: DutyCardProps) {
        const parsed = useMemo(() => parseFrequency(duty.frequency), [duty.frequency]);
        const reasonCodes = duty.rnChangeReasonCodes ?? [];
        const reasonNote = duty.rnChangeReasonNote ?? "";
        const isOverridden =
            originalState != null &&
            (originalState.selected !== duty.selected ||
                originalState.frequency !== duty.frequency);

        const handleFreqType = useCallback(
            (type: FreqType) => {
                if (readOnly) {
                    return;
                }
                const newFreq = serializeFrequency(type, type === "dpw" ? parsed.dpw : 1);
                onChangeFrequency(index, newFreq);
            },
            [readOnly, index, parsed.dpw, onChangeFrequency]
        );

        const handleDpwChange = useCallback(
            (delta: number) => {
                if (readOnly) {
                    return;
                }
                const newDpw = Math.max(1, Math.min(7, parsed.dpw + delta));
                onChangeFrequency(index, serializeFrequency("dpw", newDpw));
            },
            [readOnly, index, parsed.dpw, onChangeFrequency]
        );

        const handleToggleReason = useCallback(
            (code: PocDutyChangeReason) => {
                if (readOnly) {
                    return;
                }
                const nextCodes = reasonCodes.includes(code)
                    ? reasonCodes.filter((existing) => existing !== code)
                    : [...reasonCodes, code];
                onChangeExplanation(index, {
                    rnChangeReasonCodes: nextCodes,
                    rnChangeReasonNote: reasonNote.trim() || null
                });
            },
            [readOnly, index, reasonCodes, reasonNote, onChangeExplanation]
        );

        const handleReasonNoteChange = useCallback(
            (text: string) => {
                onChangeExplanation(index, {
                    rnChangeReasonCodes: reasonCodes,
                    rnChangeReasonNote: text
                });
            },
            [index, reasonCodes, onChangeExplanation]
        );

        return (
            <View style={[styles.dutyCard, duty.selected && styles.dutyCardSelected]}>
                <View style={styles.dutyTop}>
                    {/* Checkbox */}
                    <TouchableOpacity
                        onPress={() => !readOnly && onToggle(index)}
                        activeOpacity={0.7}
                        disabled={readOnly}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: duty.selected, disabled: readOnly }}
                        accessibilityLabel={duty.uiLabel ?? duty.name}
                        hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
                        style={[styles.checkbox, duty.selected && styles.checkboxChecked]}
                    >
                        {duty.selected ? (
                            <LinearGradient
                                colors={[...gradients.brand]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={styles.checkboxGradient}
                            >
                                <MaterialCommunityIcons name="check" size={14} color="#FFFFFF" />
                            </LinearGradient>
                        ) : null}
                    </TouchableOpacity>

                    {/* Duty info */}
                    <View style={styles.dutyInfo}>
                        {/* Name row */}
                        <View style={styles.dutyNameRow}>
                            <Text
                                style={[
                                    styles.dutyName,
                                    duty.selected && styles.dutyNameSelected,
                                    !duty.selected && styles.dutyNameUnselected
                                ]}
                            >
                                {duty.uiLabel ?? duty.name}
                            </Text>
                            {duty.aiSuggested && (
                                <LinearGradient
                                    colors={[...gradients.brandSoft]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                    style={styles.aiBadge}
                                >
                                    <Text
                                        accessibilityLabel={
                                            carryForward
                                                ? "On the current plan of care"
                                                : "AI suggested"
                                        }
                                        style={styles.aiBadgeText}
                                    >
                                        {carryForward ? "ON PLAN" : "AI"}
                                    </Text>
                                </LinearGradient>
                            )}
                            {isOverridden && (
                                <View style={styles.overrideBadge}>
                                    <Text
                                        accessibilityLabel="RN override"
                                        style={styles.overrideBadgeText}
                                    >
                                        RN Override
                                    </Text>
                                </View>
                            )}
                        </View>

                        {/* Frequency row — pointerEvents lives on style.pointerEvents
                            (the prop form is deprecated in newer RN). */}
                        <View
                            style={[
                                styles.freqRow,
                                {
                                    opacity: duty.selected ? 1 : 0.35,
                                    pointerEvents: duty.selected && !readOnly ? "auto" : "none"
                                }
                            ]}
                        >
                            {/* Every visit pill */}
                            <TouchableOpacity
                                onPress={() => handleFreqType("every")}
                                accessibilityRole="radio"
                                accessibilityState={{ selected: parsed.type === "every" }}
                                accessibilityLabel="Every visit"
                                hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
                                style={[
                                    styles.freqPill,
                                    parsed.type === "every" && styles.freqPillActive
                                ]}
                                activeOpacity={0.7}
                            >
                                <View
                                    style={[
                                        styles.freqRadio,
                                        parsed.type === "every" && styles.freqRadioActive
                                    ]}
                                >
                                    {parsed.type === "every" && (
                                        <View style={styles.freqRadioDot} />
                                    )}
                                </View>
                                <Text
                                    style={[
                                        styles.freqPillText,
                                        parsed.type === "every" && styles.freqPillTextActive
                                    ]}
                                >
                                    Every visit
                                </Text>
                            </TouchableOpacity>

                            {/* As needed pill */}
                            <TouchableOpacity
                                onPress={() => handleFreqType("needed")}
                                accessibilityRole="radio"
                                accessibilityState={{ selected: parsed.type === "needed" }}
                                accessibilityLabel="As needed"
                                hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
                                style={[
                                    styles.freqPill,
                                    parsed.type === "needed" && styles.freqPillActive
                                ]}
                                activeOpacity={0.7}
                            >
                                <View
                                    style={[
                                        styles.freqRadio,
                                        parsed.type === "needed" && styles.freqRadioActive
                                    ]}
                                >
                                    {parsed.type === "needed" && (
                                        <View style={styles.freqRadioDot} />
                                    )}
                                </View>
                                <Text
                                    style={[
                                        styles.freqPillText,
                                        parsed.type === "needed" && styles.freqPillTextActive
                                    ]}
                                >
                                    As needed
                                </Text>
                            </TouchableOpacity>

                            {/* Days/wk pill */}
                            <TouchableOpacity
                                onPress={() => handleFreqType("dpw")}
                                accessibilityRole="radio"
                                accessibilityState={{ selected: parsed.type === "dpw" }}
                                accessibilityLabel={`${parsed.dpw} visits per week`}
                                hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
                                style={[
                                    styles.freqPill,
                                    parsed.type === "dpw" && styles.freqPillActive
                                ]}
                                activeOpacity={0.7}
                            >
                                <View
                                    style={[
                                        styles.freqRadio,
                                        parsed.type === "dpw" && styles.freqRadioActive
                                    ]}
                                >
                                    {parsed.type === "dpw" && <View style={styles.freqRadioDot} />}
                                </View>
                                <Text
                                    style={[
                                        styles.freqPillText,
                                        parsed.type === "dpw" && styles.freqPillTextActive
                                    ]}
                                >
                                    Days/wk
                                </Text>
                                {parsed.type === "dpw" && (
                                    <View style={styles.dpwPicker}>
                                        <TouchableOpacity
                                            onPress={() => handleDpwChange(-1)}
                                            accessibilityRole="button"
                                            accessibilityLabel="Decrease days per week"
                                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                            style={styles.dpwBtn}
                                            activeOpacity={0.7}
                                        >
                                            <Text style={styles.dpwBtnText}>{"−"}</Text>
                                        </TouchableOpacity>
                                        <Text style={styles.dpwValue}>{parsed.dpw}</Text>
                                        <TouchableOpacity
                                            onPress={() => handleDpwChange(1)}
                                            accessibilityRole="button"
                                            accessibilityLabel="Increase days per week"
                                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                            style={styles.dpwBtn}
                                            activeOpacity={0.7}
                                        >
                                            <Text style={styles.dpwBtnText}>+</Text>
                                        </TouchableOpacity>
                                    </View>
                                )}
                            </TouchableOpacity>
                        </View>

                        {isOverridden && (
                            <View style={styles.explanationBox}>
                                <Text style={styles.explanationLabel}>
                                    Please explain why are you making this change?
                                </Text>
                                <View style={styles.reasonChips}>
                                    {POC_DUTY_CHANGE_REASONS.map((reason) => {
                                        const active = reasonCodes.includes(reason.code);
                                        return (
                                            <TouchableOpacity
                                                key={reason.code}
                                                onPress={() => handleToggleReason(reason.code)}
                                                disabled={readOnly}
                                                activeOpacity={0.75}
                                                accessibilityRole="button"
                                                accessibilityState={{ selected: active }}
                                                accessibilityLabel={reason.label}
                                                style={[
                                                    styles.reasonChip,
                                                    active && styles.reasonChipActive
                                                ]}
                                            >
                                                <Text
                                                    style={[
                                                        styles.reasonChipText,
                                                        active && styles.reasonChipTextActive
                                                    ]}
                                                >
                                                    {reason.label}
                                                </Text>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>
                                <TextInput
                                    mode="outlined"
                                    dense
                                    multiline
                                    value={reasonNote}
                                    onChangeText={handleReasonNoteChange}
                                    editable={!readOnly}
                                    placeholder="Other details..."
                                    style={styles.reasonInput}
                                    outlineStyle={styles.reasonInputOutline}
                                />
                            </View>
                        )}
                    </View>
                </View>
            </View>
        );
    },
    (prev, next) =>
        prev.duty.name === next.duty.name &&
        prev.duty.selected === next.duty.selected &&
        prev.duty.frequency === next.duty.frequency &&
        prev.duty.aiSuggested === next.duty.aiSuggested &&
        prev.carryForward === next.carryForward &&
        prev.duty.rnChangeReasonNote === next.duty.rnChangeReasonNote &&
        (prev.duty.rnChangeReasonCodes ?? []).join("|") ===
            (next.duty.rnChangeReasonCodes ?? []).join("|") &&
        prev.duty.uiLabel === next.duty.uiLabel &&
        prev.duty.code === next.duty.code &&
        prev.duty.id === next.duty.id &&
        prev.originalState?.selected === next.originalState?.selected &&
        prev.originalState?.frequency === next.originalState?.frequency &&
        prev.readOnly === next.readOnly &&
        prev.index === next.index
);

// ---------------------------------------------------------------------------
// Category Section (accordion)
// ---------------------------------------------------------------------------

interface CategorySectionProps {
    category: string;
    duties: MergedPocDuty[];
    /** See PocDutyListProps.carryForward. */
    carryForward?: boolean;
    dutyIndices: number[];
    aiBaseline: Map<string, { selected: boolean; frequency: string }> | null;
    onToggle: (index: number) => void;
    onChangeFrequency: (index: number, frequency: PocFrequency) => void;
    onChangeExplanation: (
        index: number,
        explanation: {
            rnChangeReasonCodes: PocDutyChangeReason[];
            rnChangeReasonNote: string | null;
        }
    ) => void;
    onSetCategorySelection: (keys: Set<string>, selected: boolean) => void;
    readOnly: boolean;
}

const CategorySection = React.memo(function CategorySection({
    category,
    duties,
    carryForward = false,
    dutyIndices,
    aiBaseline,
    onToggle,
    onChangeFrequency,
    onChangeExplanation,
    onSetCategorySelection,
    readOnly
}: CategorySectionProps) {
    const [open, setOpen] = useState(true);
    const chevronRotation = useRef(new Animated.Value(1)).current;

    const toggleOpen = useCallback(() => {
        // Animate the layout change so the body opens/closes smoothly instead
        // of snapping abruptly. LayoutAnimation is idempotent on iOS; on
        // Android it requires the experimental flag (enabled at module load).
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        const nextOpen = !open;
        setOpen(nextOpen);
        Animated.timing(chevronRotation, {
            toValue: nextOpen ? 1 : 0,
            duration: 200,
            useNativeDriver: true
        }).start();
    }, [open, chevronRotation]);

    const selectedCount = duties.filter((d) => d.selected).length;
    const rotate = chevronRotation.interpolate({
        inputRange: [0, 1],
        outputRange: ["0deg", "180deg"]
    });

    const handleSelectAll = useCallback(() => {
        const keys = new Set(duties.filter((d) => !d.selected).map(dutyKey));
        if (keys.size > 0) {
            onSetCategorySelection(keys, true);
        }
    }, [duties, onSetCategorySelection]);

    const handleDeselectAll = useCallback(() => {
        const keys = new Set(duties.filter((d) => d.selected).map(dutyKey));
        if (keys.size > 0) {
            onSetCategorySelection(keys, false);
        }
    }, [duties, onSetCategorySelection]);

    return (
        <View style={styles.categoryContainer}>
            {/* Category header */}
            <TouchableOpacity
                onPress={toggleOpen}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityState={{ expanded: open }}
                accessibilityLabel={`${category} category, ${selectedCount} of ${duties.length} selected`}
                style={styles.categoryHeader}
            >
                <View style={styles.catLeft}>
                    {getCategoryIcon(category)}
                    <View>
                        <Text style={styles.catLabel}>{category}</Text>
                        <Text style={styles.catCount}>{duties.length} duties</Text>
                    </View>
                </View>
                <View style={styles.catRight}>
                    <View style={styles.catSelectedBadge}>
                        <Text style={styles.catSelectedText}>
                            {selectedCount}/{duties.length}
                        </Text>
                    </View>
                    <Animated.View style={{ transform: [{ rotate }] }}>
                        <MaterialCommunityIcons
                            name="chevron-down"
                            size={20}
                            color={colors.textTertiary}
                        />
                    </Animated.View>
                </View>
            </TouchableOpacity>

            {/* Category body */}
            {open && (
                <>
                    {/* Select / Deselect buttons */}
                    {!readOnly && (
                        <View style={styles.catActions}>
                            <TouchableOpacity
                                onPress={handleSelectAll}
                                accessibilityRole="button"
                                accessibilityLabel={`Select all ${category} duties`}
                                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                                style={styles.catActionBtn}
                                activeOpacity={0.7}
                            >
                                <Text style={styles.catActionText}>Select All</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                onPress={handleDeselectAll}
                                accessibilityRole="button"
                                accessibilityLabel={`Deselect all ${category} duties`}
                                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                                style={styles.catActionBtn}
                                activeOpacity={0.7}
                            >
                                <Text style={styles.catActionText}>Deselect All</Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* Duty cards */}
                    <View style={styles.catDuties}>
                        {duties.map((duty, i) => (
                            <DutyCard
                                key={dutyKey(duty)}
                                duty={duty}
                                carryForward={carryForward}
                                index={dutyIndices[i]}
                                originalState={aiBaseline?.get(dutyKey(duty))}
                                onToggle={onToggle}
                                onChangeFrequency={onChangeFrequency}
                                onChangeExplanation={onChangeExplanation}
                                readOnly={readOnly}
                            />
                        ))}
                    </View>
                </>
            )}
        </View>
    );
});

// ---------------------------------------------------------------------------
// Main PocDutyList
// ---------------------------------------------------------------------------

export function PocDutyList({
    duties,
    carryForward = false,
    specialInstructions,
    aiBaseline,
    autosaveStatus,
    onToggleDuty,
    onChangeFrequency,
    onChangeDutyExplanation,
    onChangeInstructions,
    onSetCategorySelection,
    onConfirm,
    saving,
    readOnly,
    readOnlyReason
}: PocDutyListProps) {
    const insets = useSafeAreaInsets();
    const [barHeight, setBarHeight] = useState(120);

    const selectedCount = useMemo(() => duties.filter((d) => d.selected).length, [duties]);
    const totalCount = duties.length;

    // Group duties by category — accept that categorizedDuties is recreated
    // on every duty change; the savings live inside the memoized DutyCards.
    const categorizedDuties = useMemo(() => {
        return CATEGORY_ORDER.map((cat) => {
            const catDuties: MergedPocDuty[] = [];
            const catIndices: number[] = [];
            duties.forEach((d, i) => {
                if (d.category === cat) {
                    catDuties.push(d);
                    catIndices.push(i);
                }
            });
            return { category: cat, duties: catDuties, indices: catIndices };
        }).filter((g) => g.duties.length > 0);
    }, [duties]);

    const canConfirm = selectedCount >= 5 && !saving;
    const bottomCountText =
        selectedCount < 5
            ? `Select at least 5 duties to confirm (${selectedCount}/5)`
            : `${selectedCount} of ${totalCount} duties selected`;

    return (
        <KeyboardAvoidingView
            style={styles.root}
            behavior={Platform.OS === "ios" ? "padding" : undefined}
            keyboardVerticalOffset={Platform.OS === "ios" ? 12 : 0}
        >
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                automaticallyAdjustKeyboardInsets={true}
            >
                {/* Summary header */}
                <LinearGradient
                    colors={[...gradients.surface]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.summaryHeader}
                >
                    <View style={styles.badgeRow}>
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.aiBadgeHeader}
                        >
                            <Text style={styles.aiBadgeHeaderText}>
                                {carryForward ? "CURRENT PLAN OF CARE" : "AI GENERATED"}
                            </Text>
                        </LinearGradient>
                    </View>
                    <Text style={styles.summaryCount}>
                        {selectedCount} of {totalCount} duties selected
                    </Text>
                    {/* Assessment tags */}
                    <View style={styles.tagsRow}>
                        <View style={styles.tag}>
                            <Text style={styles.tagText}>Vital Signs</Text>
                        </View>
                        <View style={styles.tag}>
                            <Text style={styles.tagText}>Mobility</Text>
                        </View>
                        <View style={styles.tag}>
                            <Text style={styles.tagText}>Cognition</Text>
                        </View>
                        <View style={styles.tag}>
                            <Text style={styles.tagText}>Medications</Text>
                        </View>
                    </View>
                </LinearGradient>

                {readOnly && (
                    <View style={styles.readOnlyBanner}>
                        <MaterialCommunityIcons
                            name="lock-outline"
                            size={18}
                            color={colors.info600}
                        />
                        <View style={styles.readOnlyCopy}>
                            <Text style={styles.readOnlyTitle}>Plan of Care is view only</Text>
                            <Text style={styles.readOnlyText}>
                                {readOnlyReason ??
                                    "This visit is locked, so duties and notes cannot be changed."}
                            </Text>
                        </View>
                    </View>
                )}

                {/* Category sections */}
                {categorizedDuties.map(({ category, duties: catDuties, indices }) => (
                    <CategorySection
                        key={category}
                        category={category}
                        duties={catDuties}
                        carryForward={carryForward}
                        dutyIndices={indices}
                        aiBaseline={aiBaseline}
                        onToggle={onToggleDuty}
                        onChangeFrequency={onChangeFrequency}
                        onChangeExplanation={onChangeDutyExplanation}
                        onSetCategorySelection={onSetCategorySelection}
                        readOnly={readOnly}
                    />
                ))}

                {/* Special Instructions */}
                <View style={styles.instructionsContainer}>
                    <Text style={styles.instructionsLabel}>Special Instructions (optional)</Text>
                    <TextInput
                        mode="outlined"
                        multiline
                        numberOfLines={3}
                        value={specialInstructions}
                        onChangeText={onChangeInstructions}
                        disabled={readOnly}
                        style={[
                            styles.instructionsInput,
                            Platform.OS === "android" && { minHeight: 72 }
                        ]}
                        placeholder="Add any special care instructions..."
                    />
                </View>

                {/* Spacer for sticky bottom bar — derived from measured bar
                    height + safe-area inset so the last duty card and the
                    instructions field aren't clipped on devices with a home
                    indicator. */}
                <View style={{ height: barHeight + insets.bottom }} />
            </ScrollView>

            {/* Sticky bottom bar */}
            {!readOnly && (
                <View
                    onLayout={(e) => setBarHeight(e.nativeEvent.layout.height)}
                    style={[styles.bottomBar, { paddingBottom: insets.bottom + 12 }]}
                >
                    <View style={styles.bottomCountRow}>
                        <Text style={styles.bottomCount}>{bottomCountText}</Text>
                        {autosaveStatus === "saving" && (
                            <Text style={styles.autosaveText}>· Saving…</Text>
                        )}
                        {autosaveStatus === "saved" && (
                            <Text style={[styles.autosaveText, styles.autosaveSaved]}>· Saved</Text>
                        )}
                        {autosaveStatus === "error" && (
                            <Text style={[styles.autosaveText, styles.autosaveError]}>
                                · Save failed
                            </Text>
                        )}
                    </View>
                    <TouchableOpacity
                        onPress={onConfirm}
                        activeOpacity={0.85}
                        disabled={!canConfirm}
                        accessibilityRole="button"
                        accessibilityState={{ disabled: !canConfirm, busy: saving }}
                        accessibilityLabel={
                            selectedCount < 5
                                ? `Confirm disabled, select at least 5 duties, currently ${selectedCount}`
                                : "Confirm Plan of Care"
                        }
                        style={{ width: "100%", opacity: selectedCount < 5 ? 0.5 : 1 }}
                    >
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.confirmBtn}
                        >
                            {saving ? <ActivityIndicator size="small" color="#FFFFFF" /> : null}
                            <Text style={styles.confirmBtnText}>Confirm Plan of Care</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </View>
            )}
        </KeyboardAvoidingView>
    );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
    root: {
        flex: 1
    },
    scrollView: {
        flex: 1
    },
    scrollContent: {
        paddingBottom: 16
    },

    // ── Summary Header ──
    summaryHeader: {
        margin: 16,
        marginBottom: 0,
        padding: 16,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        ...shadows.sm
    },
    badgeRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 10
    },
    aiBadgeHeader: {
        paddingVertical: 3,
        paddingHorizontal: 10,
        borderRadius: radius.full
    },
    aiBadgeHeaderText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: "#FFFFFF",
        letterSpacing: 0.8,
        textTransform: "uppercase"
    },
    summaryCount: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    tagsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 4,
        marginTop: 8
    },
    tag: {
        backgroundColor: colors.teal50,
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: radius.full
    },
    tagText: {
        fontSize: 10,
        fontFamily: fonts.semiBold,
        color: colors.teal600
    },
    readOnlyBanner: {
        marginHorizontal: 16,
        marginTop: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderWidth: 1,
        borderColor: colors.info400,
        borderRadius: radius.md,
        backgroundColor: colors.info50,
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10
    },
    readOnlyCopy: {
        flex: 1
    },
    readOnlyTitle: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.info600,
        marginBottom: 2
    },
    readOnlyText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 18
    },

    // ── Category ──
    categoryContainer: {
        marginHorizontal: 16,
        marginTop: 16
    },
    categoryHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 14,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.md,
        marginBottom: 4,
        ...shadows.sm
    },
    catLeft: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10
    },
    catLabel: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    catCount: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    catRight: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8
    },
    catSelectedBadge: {
        backgroundColor: colors.teal50,
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: radius.full
    },
    catSelectedText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.teal600
    },
    catActions: {
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: 8,
        paddingVertical: 6,
        paddingHorizontal: 14,
        backgroundColor: colors.surface,
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderColor: colors.border
    },
    catActionBtn: {
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface
    },
    catActionText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary
    },
    catDuties: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: colors.border,
        borderBottomLeftRadius: radius.md,
        borderBottomRightRadius: radius.md,
        backgroundColor: colors.surface,
        overflow: "hidden",
        ...shadows.sm
    },

    // ── Duty Card ──
    dutyCard: {
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    dutyCardSelected: {
        backgroundColor: "rgba(69,210,176,0.03)"
    },
    dutyTop: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10
    },

    // ── Checkbox ──
    checkbox: {
        width: 22,
        height: 22,
        borderRadius: radius.xs,
        borderWidth: 2,
        borderColor: colors.borderStrong,
        backgroundColor: colors.surface,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1,
        overflow: "hidden"
    },
    checkboxChecked: {
        borderColor: "transparent"
    },
    checkboxGradient: {
        width: 22,
        height: 22,
        alignItems: "center",
        justifyContent: "center"
    },

    // ── Duty Info ──
    dutyInfo: {
        flex: 1,
        minWidth: 0
    },
    dutyNameRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
        marginBottom: 2
    },
    dutyName: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    },
    dutyNameSelected: {
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    dutyNameUnselected: {
        color: colors.textTertiary
    },
    aiBadge: {
        paddingVertical: 1,
        paddingHorizontal: 6,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: "rgba(69,210,176,0.2)"
    },
    aiBadgeText: {
        fontSize: 9,
        fontFamily: fonts.bold,
        color: colors.teal600,
        letterSpacing: 0.3
    },
    overrideBadge: {
        paddingVertical: 1,
        paddingHorizontal: 6,
        backgroundColor: colors.pink50,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: "rgba(231,72,245,0.2)"
    },
    overrideBadgeText: {
        fontSize: 9,
        fontFamily: fonts.bold,
        color: "#C026D3",
        letterSpacing: 0.3
    },

    // ── Frequency ──
    freqRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 8,
        alignItems: "center"
    },
    freqPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        paddingVertical: 5,
        paddingHorizontal: 10,
        backgroundColor: colors.background,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.full
    },
    freqPillActive: {
        backgroundColor: colors.teal50,
        borderColor: colors.teal400
    },
    freqPillText: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    freqPillTextActive: {
        color: colors.teal600,
        fontFamily: fonts.semiBold
    },
    freqRadio: {
        width: 14,
        height: 14,
        borderRadius: 7,
        borderWidth: 2,
        borderColor: colors.borderStrong,
        alignItems: "center",
        justifyContent: "center"
    },
    freqRadioActive: {
        borderColor: colors.teal400
    },
    freqRadioDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.teal400
    },

    // ── RN Change Explanation ──
    explanationBox: {
        marginTop: 10,
        padding: 10,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.sm,
        backgroundColor: colors.background
    },
    explanationLabel: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary,
        marginBottom: 8
    },
    reasonChips: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6
    },
    reasonChip: {
        paddingVertical: 5,
        paddingHorizontal: 9,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface
    },
    reasonChipActive: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    reasonChipText: {
        fontSize: 10,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    reasonChipTextActive: {
        fontFamily: fonts.semiBold,
        color: colors.teal600
    },
    reasonInput: {
        marginTop: 8,
        minHeight: 44,
        backgroundColor: colors.surface,
        fontSize: 12
    },
    reasonInputOutline: {
        borderRadius: radius.sm,
        borderColor: colors.border
    },

    // ── DPW Picker ──
    dpwPicker: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        marginLeft: 2
    },
    dpwBtn: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.borderStrong,
        backgroundColor: colors.surface,
        alignItems: "center",
        justifyContent: "center"
    },
    dpwBtnText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        lineHeight: 15
    },
    dpwValue: {
        width: 22,
        textAlign: "center",
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.teal600
    },

    // ── Special Instructions ──
    instructionsContainer: {
        marginHorizontal: 16,
        marginTop: 16,
        padding: 14,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.md,
        ...shadows.sm
    },
    instructionsLabel: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: 8
    },
    instructionsInput: {
        backgroundColor: colors.background
    },

    // ── Sticky Bottom Bar ──
    // paddingBottom is applied inline from useSafeAreaInsets().bottom + 12.
    bottomBar: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        paddingTop: 12,
        paddingHorizontal: 16,
        backgroundColor: "rgba(255,255,255,0.95)",
        borderTopWidth: 1,
        borderTopColor: colors.border,
        alignItems: "center"
    },
    bottomCountRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        marginBottom: 8
    },
    bottomCount: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        textAlign: "center"
    },
    autosaveText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    },
    autosaveSaved: {
        color: colors.teal600
    },
    autosaveError: {
        color: colors.error500
    },
    confirmBtn: {
        width: "100%",
        paddingVertical: 15,
        borderRadius: radius.lg,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        ...shadows.brand
    },
    confirmBtnText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    }
});
